<script setup lang="ts">
import { useStorage } from "@vueuse/core";
import { computed, onMounted, onUnmounted } from "vue";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { showPortalButtonKey } from "~/utils/constants";
import { getModeDisplay, getModeForTitle } from "~/utils/miscUtils";
import { getEnvConfig } from "~/services/appConfig";
import IntakeTopBar from "../components/IntakeTopBar.vue";

// default is false, but use the showPortalButton from localstorage.
// set to true when the user goes to the url `/finder/hmb-login-access`
// see ~/router/mainRouter.ts
const showPortalLoginButton = useStorage(showPortalButtonKey, false);

const showWarning = getEnvConfig().env !== "live";

const myInfoStore = useMyInfoStore();

const windowTitle = computed(() => {
  return myInfoStore.currentSysOrg?.name ?? "Manifast™ Business Tools";
});

onMounted(async () => {
  await myInfoStore.load();
  // add class to the body when the page is loaded
  document.body.classList.add("intake");
  document.title = windowTitle.value + getModeForTitle();
});

onUnmounted(() => {
  document.body.classList.remove("intake");
});
</script>
<template>
  <el-container class="mainContainer intake">
    <div v-if="showWarning" class="warningContainer">
      <div class="warning">{{ getModeDisplay() }}</div>
    </div>
    <el-header class="border homeHeader">
      <IntakeTopBar :show-portal-login-button="showPortalLoginButton" />
    </el-header>

    <el-container>
      <el-main class="border homeMain">
        <RouterView />
      </el-main>
    </el-container>
  </el-container>
</template>
<style lang="less">
section.intake {
  --el-font-size-base: 1.3rem; // match host site size
  --el-checkbox-font-size: 1.3rem; // used?
  font-family: "Poppins";
  font-size: var(--el-font-size-base);

  @primary-color: #2a2829;
  --el-color-primary: @primary-color;
  --el-color-primary-light-3: lighten(@primary-color, 10%);
  --el-color-primary-light-5: lighten(@primary-color, 50%);
  --el-color-primary-light-7: lighten(@primary-color, 55%);
  --el-color-primary-light-8: lighten(@primary-color, 60%);
  --el-color-primary-light-9: lighten(@primary-color, 75%);
  --el-color-primary-dark-2: darken(@primary-color, 10%);
  --el-color-primary-dark-3: darken(@primary-color, 15%);

  @yyc-green: #4c8483;
  --yyc-green: @yyc-green;
  --yyc-green-light: lighten(@yyc-green, 40%);

  --yych-background-color: #e0e0db;
  --yych-background-color-light: #f5f5f5;
  --el-color-primary-light-9: #f5f5f5;

  --subheading-color: #253551;

  @info-color: #4b8483;
  --el-color-info: @info-color;
  --el-color-info-light-3: lighten(@info-color, 30%);
  --el-color-info-light-5: lighten(@info-color, 40%);
  --el-color-info-light-7: lighten(@info-color, 50%);
  --el-color-info-light-8: lighten(@info-color, 60%);
  --el-color-info-light-9: lighten(@info-color, 70%);
  --el-color-info-dark-2: darken(@info-color, 20%);

  /* adjusted to meet AAA contrast ratio with primary-light-9 */
  --el-color-danger: #a10000;
  /* --el-color-primary: #9f806b; */
  --silveraColorPrimary: #536e82;
  --el-text-color-primary: #536e82;
  --el-text-color-regular: #606266;
  --el-text-color-secondary: #909399;

  // so the checkbox text is black
  --el-text-color-regular: #2a2829;

  color: var(--el-text-color-regular);

  .el-header {
    --el-header-height: 88px;
  }
  --el-header-height: 88px;

  button {
    font-family: "Poppins";
  }

  .FilesComponent {
    .el-upload-list__item-name {
      .el-icon {
        color: var(--yyc-green);
      }
    }
  }
}

.intake {
  &.mainContainer {
    .warningContainer {
      width: 100%;
      z-index: 10;
      color: var(--el-color-primary-light-8);
      font-size: 1rem;
      padding: 0 1em;
      background-color: var(--subheading-color);
      .warning {
        margin: auto;
        width: fit-content;
        opacity: 0.7;
      }
    }
    .homeHeader {
      background-color: var(--yych-background-color);
      box-shadow: 0px 0px 6px 4px hsl(0deg 0% 0% / 15%);
      z-index: 101;
    }
    // This is the height - header height necessary for scrolling
    .homeMain {
      height: calc(100vh - var(--el-header-height));
    }
  }

  .right {
    margin-top: 0.25em;
    align-items: end;
    display: flex;
    flex-direction: column-reverse;
  }
}

body.intake {
  > .el-container {
    overflow-y: auto;
    overflow-x: hidden;
  }

  .el-message-box {
    .el-message-box__title {
      font-size: 1.5rem;
    }
    .el-message-box__message p {
      font-size: 1.3rem;
      line-height: normal;
      margin: 0 1em 0 0.5em;
    }
    .el-button {
      font-size: 1rem;
    }
  }

  // This solves the issue of the checkmark in the checkbox being too small and offset
  // .el-checkbox__input.is-checked .el-checkbox__inner:after {
  //   transform: rotate(45deg) scaleY(1); // remove the translate()
  // }

  // need both so the icon and text are the same size
  .el-message,
  .el-message__content {
    font-size: 1.3rem;
  }
}
</style>
